import * as React from 'react';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { Translate } from '../../shared/LanguageService';
import LogsModal from '../logs/LogsModal';
import CopywritersAPI from './api/copywritersAPI';
import CopywritersList from './CopywritersList';
import CopywritersModal from './CopywritersModal';
import CreateNewCopywritersModal from './CopywritersModal';

function CopywritersPage() {
    const queryClient = useQueryClient();
    const { isLoading, isError, error, data, refetch } = useQuery(['listCopywriters'], () => {
        return CopywritersAPI.getCopywriters();
    });
    const deleteCopywriterMutation = useMutation(
        (id: number) => CopywritersAPI.deleteCopywriter(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('listCopywriters');
                refetch();
            }
        }
    );
    const language = useQuery(['getAvaiableLanguages'], () =>
        CopywritersAPI.getAvailableLanguage()
    );
    const [copywriterId, setCopywriterId] = useState(null);
    const [showCreateCopywriterModal, setShowCreateCopywriterModal] = useState(false);
    const [showEditCopywriterModal, setShowEditCopywriterModal] = useState(false);
    const [showLogsModal, setShowLogs] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

    React.useEffect(() => {
        if (!showLogsModal) {
            setCopywriterId(null);
        }
    }, [showLogsModal]);

    const toggleCreateCopywriterModal = () => {
        if (showCreateCopywriterModal) {
            setShowCreateCopywriterModal(false);
            refetch();
        } else {
            setShowCreateCopywriterModal(true);
        }
    };

    const toggleEditCopywriterModal = () => {
        if (showEditCopywriterModal) {
            setShowEditCopywriterModal(false);
            setCopywriterId(null);
            refetch();
        } else {
            setShowEditCopywriterModal(true);
        }
    };

    const toggleLogsModal = (newValue?) => {
        if (showLogsModal) {
            setShowLogs(false);
            setCopywriterId(null);
            refetch();
        } else {
            setCopywriterId(newValue);
            setShowLogs(true);
        }
    };

    if (isError) {
        console.error('error', error);
    }

    if (isLoading) {
        return <p>Loading</p>;
    }

    const editCopywriter = (newValue) => {
        setCopywriterId(newValue);
        toggleEditCopywriterModal();
    };

    const deleteCopywriter = () => {
        if (copywriterId > 0) {
            deleteCopywriterMutation.mutate(copywriterId);
            setCopywriterId(null);
            refetch();
        }
    };

    const renderConfirmModal = (id: number) => {
        setCopywriterId(id);
        setShowDeleteConfirmationDialog(true);
    };

    return (
        <div>
            <LogsModal
                id={copywriterId}
                type="CopywriterId"
                shouldShowModal={showLogsModal}
                closeModal={toggleLogsModal}
                userHeader={Translate('role/copywriter')}
            />

            <CreateNewCopywritersModal
                showModal={showCreateCopywriterModal}
                toggleModal={toggleCreateCopywriterModal}
                copywriterId={null}
                availableLanguagesKeys={language.data}
            />

            <CopywritersModal
                showModal={showEditCopywriterModal}
                toggleModal={toggleEditCopywriterModal}
                copywriterId={copywriterId}
                availableLanguagesKeys={language.data}
            />

            <ConfirmationModal
                showDialog={showDeleteConfirmationDialog}
                title={Translate('copywriter/modal/remove/confirmation/title')}
                text={Translate('copywriter/modal/remove/confirmation/description')}
                doButtonLabel={Translate('copywriter/list/row/button-label/delete')}
                cancelButtonLabel={Translate('copywriter/list/row/button-label/abort')}
                doButtonStyle="danger"
                do={() => {
                    deleteCopywriter();
                    setShowDeleteConfirmationDialog(false);
                }}
                closeModal={() => {
                    setShowDeleteConfirmationDialog(false);
                }}
            />

            <h1>{Translate('copywriter/page/title/copywriter')}</h1>
            <Button
                bsSize="large"
                className="pull-right"
                onClick={toggleCreateCopywriterModal}
                bsStyle="success"
            >
                {Translate('copywriter/page/button-label/add-copywriter')}
            </Button>
            <CopywritersList
                copywriters={data}
                editCopywriters={editCopywriter}
                viewlogs={toggleLogsModal}
                deleteCopywriters={renderConfirmModal}
            />
        </div>
    );
}

export default CopywritersPage
